import { Link } from "react-router-dom";
import type { Approval, ApprovalType } from "@/types";
import { cn } from "@/lib/utils";

const TYPE_ICON: Record<ApprovalType, { symbol: string; bg: string; color: string }> = {
  transfer: { symbol: "↑", bg: "var(--ads-tint-blue)", color: "var(--ads-info)" },
  wallet:  { symbol: "⊞", bg: "var(--ads-tint-green)", color: "var(--ads-viz-green)" },
  policy:  { symbol: "⚙", bg: "var(--ads-warning-soft)", color: "var(--ads-viz-amber-strong)" },
};

const SLA_META: Record<string, "ok" | "warn"> = {
  ok: "ok", warn: "warn",
};

export function ApprovalCard({ approval }: { approval: Approval }) {
  const type = TYPE_ICON[approval.type];
  const sla = SLA_META[approval.slaSeverity] ?? "ok";
  const card = (
    <div
      className={cn(
        "rounded-lg border bg-surface p-4",
        approval.urgent ? "border-viz-amber bg-tint-amber" : "border-line"
      )}
    >
      <div className="flex flex-row items-start gap-3">
        <span
          className="flex-shrink-0 rounded-[4px] px-2 py-[3px] text-[16px] font-bold leading-none"
          style={{ background: type.bg, color: type.color }}
        >
          {type.symbol}
        </span>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-semibold text-ink leading-tight">{approval.title}</div>
          {approval.meta && <div className="mt-0.5 text-xs text-muted">{approval.meta}</div>}
        </div>
        {approval.slaLeft && (
          <span
            className={cn(
              "shrink-0 inline-flex items-center rounded-[4px] px-2 py-[3px] text-[11px] font-bold",
              sla === "ok"
                ? "bg-success-soft text-success"
                : "bg-warning-soft text-warning"
            )}
          >
            {approval.slaLeft}
          </span>
        )}
      </div>
      {approval.progressPercent != null && (
        <div className="mt-2 h-[6px] w-20 overflow-hidden rounded-full bg-line">
          <div
            className={cn("h-full rounded-full", approval.urgent ? "bg-warning" : "bg-success")}
            style={{ width: `${approval.progressPercent}%` }}
          />
        </div>
      )}
      {approval.hint && <div className="mt-1 text-xs text-muted">{approval.hint}</div>}
    </div>
  );
  if (approval.type === "transfer") {
    return (
      <Link
        to={`/approvals/${approval.id}`}
        className="block rounded-lg outline-none transition-[box-shadow] focus-visible:shadow-[0_0_0_3px_var(--ads-primary-soft)]"
      >
        {card}
      </Link>
    );
  }
  return card;
}

export function ApprovedRow({ approval }: { approval: Approval }) {
  const statusLabel = approval.status === "approved" ? "Approved" : "Rejected";
  return (
    <div className="flex items-center justify-between gap-3 border-b border-line px-4 py-3 last:border-b-0">
      <div className="flex min-w-0 items-center gap-3">
        <span
          className={cn(
            "grid size-8 shrink-0 place-items-center rounded-full",
            approval.status === "approved" ? "bg-success-soft" : "bg-danger-soft"
          )}
        >
          {approval.status === "approved" ? (
            <span className="text-xs font-extrabold text-success" aria-hidden>✓</span>
          ) : (
            <span className="text-xs font-extrabold text-danger" aria-hidden>✕</span>
          )}
        </span>
        <div className="min-w-0">
          <div className="text-sm font-semibold text-ink">{approval.title}</div>
          {approval.meta && <div className="text-xs text-muted">{approval.meta}</div>}
        </div>
      </div>
      <span className={cn(
        "inline-flex items-center rounded-full px-2 py-0.5 text-xs font-bold",
        approval.status === "approved" ? "bg-success-soft text-success" : "bg-danger-soft text-danger"
      )}>
        {statusLabel}
      </span>
    </div>
  );
}